import React, { useState, useEffect, useCallback } from 'react';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  ComposedChart,
} from 'recharts';
import { Card, CardContent, CardHeader } from './ui/card';
import { stockAPI } from '../services/api';
import StrategyHeader from './trading-chart/StrategyHeader';
import ChartLegend from './trading-chart/ChartLegend';
import {
  addTechnicalIndicators,
  getStrategyColor,
  strategyOverlayFlags,
} from './trading-chart/indicators';

// Hoisted-once style/config objects so Recharts doesn't treat them as
// "new" on every render — meaningful FPS improvement on watchlist pages.
const CHART_MARGIN = { top: 5, right: 5, left: -20, bottom: 5 };
const AXIS_TICK = { fontSize: 10, fill: '#6b7280' };
const Y_DOMAIN = ['auto', 'auto'];
const TOOLTIP_STYLE = {
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  fontSize: '12px',
};
const RESISTANCE_LABEL = { value: 'Resistance', fontSize: 10, fill: '#ef4444' };
const SUPPORT_LABEL = { value: 'Support', fontSize: 10, fill: '#22c55e' };
const tooltipFormatter = (value) => (value ? `$${value.toFixed(2)}` : 'N/A');

const TradingChart = ({ symbol, strategy }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadChartData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await stockAPI.getHistory(symbol, '3mo');
      if (response?.closes && response.dates) {
        const data = response.dates.map((date, index) => ({
          date: new Date(date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          }),
          price: response.closes[index],
          volume: response.volumes ? response.volumes[index] : 0,
        }));

        setChartData(addTechnicalIndicators(data, strategy));
      }
    } catch (error) {
      console.warn('Error loading chart data:', error);
    } finally {
      setLoading(false);
    }
  }, [symbol, strategy]);

  useEffect(() => {
    if (symbol) loadChartData();
  }, [symbol, loadChartData]);

  const color = getStrategyColor(strategy.category);
  const { hasMA, hasBB, hasSupport } = strategyOverlayFlags(strategy);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <StrategyHeader strategy={strategy} color={color} />
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center text-gray-500">
            Loading chart...
          </div>
        </CardContent>
      </Card>
    );
  }

  const gradientId = `gradient-${strategy.id}`;
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <StrategyHeader strategy={strategy} color={color} />
      </CardHeader>
      <CardContent className="pt-0">
        <ResponsiveContainer width="100%" height={250}>
          <ComposedChart data={chartData} margin={CHART_MARGIN}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis
              dataKey="date"
              tick={AXIS_TICK}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={AXIS_TICK}
              tickLine={false}
              domain={Y_DOMAIN}
            />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={tooltipFormatter}
            />

            {hasBB && (
              <>
                <Line type="monotone" dataKey="upperBand" stroke="#ef4444" strokeWidth={1} dot={false} strokeDasharray="3 3" name="Upper Band" />
                <Line type="monotone" dataKey="lowerBand" stroke="#22c55e" strokeWidth={1} dot={false} strokeDasharray="3 3" name="Lower Band" />
                <Line type="monotone" dataKey="middleBand" stroke="#6b7280" strokeWidth={1} dot={false} strokeDasharray="2 2" name="Middle Band" />
              </>
            )}

            {hasMA && (
              <>
                <Line type="monotone" dataKey="ma20" stroke="#f59e0b" strokeWidth={2} dot={false} name="MA20" />
                <Line type="monotone" dataKey="ma50" stroke="#3b82f6" strokeWidth={2} dot={false} name="MA50" />
              </>
            )}

            {hasSupport && chartData.length > 0 && (
              <>
                <ReferenceLine
                  y={chartData[0].resistance}
                  stroke="#ef4444"
                  strokeDasharray="3 3"
                  label={RESISTANCE_LABEL}
                />
                <ReferenceLine
                  y={chartData[0].support}
                  stroke="#22c55e"
                  strokeDasharray="3 3"
                  label={SUPPORT_LABEL}
                />
              </>
            )}

            <Area
              type="monotone"
              dataKey="price"
              stroke={color}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              name="Price"
            />
          </ComposedChart>
        </ResponsiveContainer>

        <ChartLegend color={color} hasMA={hasMA} hasBB={hasBB} hasSupport={hasSupport} />
      </CardContent>
    </Card>
  );
};

export default TradingChart;
